import { useCallback, useEffect, useRef } from 'react';
import { WebSocketClient } from '@/lib/ws/wsClient';
import type { DetectedBoard } from '@/stores/useBoardEditorStore';

interface UseBoardWebSocketLocalOptions {
    enabled: boolean;
    onLog: (message: string) => void;
    onBoardsDetected: (boards: DetectedBoard[]) => void;
    onWebSocketConnected: (connected: boolean) => void;
    onBridgeConnected: (connected: boolean) => void;
    onOnlineIdsUpdated?: (onlineIds: string[]) => void;
    // 診断表示(設定モーダル)・必要バージョンとの比較のために、接続中のブリッジ自身の
    // バージョンを受け取る。ブリッジがrequest_bridge_versionに応答してくれる前提。
    onBridgeVersion?: (version: string) => void;
    // ブリッジがbridge_update.pyの定期チェックでキャッシュしている「今知りうる最新
    // バージョン」を受け取る(古いブリッジは未対応で応答が来ないこともある)。
    onLatestBridgeVersion?: (version: string | null) => void;
    // 設定モーダルの「今すぐ更新を確認」ボタンから呼ばれるcheck_bridge_update_nowの結果。
    // 更新が実際に適用された場合はブリッジプロセスごと再起動されるため応答が来ないまま
    // 切断される(それ自体が「更新中」のシグナルになる)。
    onBridgeUpdateCheckResult?: (result: {
        update_available: boolean;
        latest_version?: string;
        message: string;
    }) => void;
    hasSelectedBoard: boolean;
    selectedBoard: 'orange' | 'blue';
    onPortUpdate: (port: string | null) => void;
    // ブリッジがchip_id読み取り/アップロードのために、指定ポートのシリアルモニターを
    // 一時停止(paused)・再開(resumed)した際に呼ばれる。モニター窓側のグレーアウト表示に使う。
    onSerialMonitorStatus?: (port: string, status: string) => void;
    pollingInterval?: number;
    getActiveRequestId: () => string | null;
    onUploadEvent: (e: {
        source: 'localhost';
        kind: 'progress' | 'result';
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
}

interface UseBoardWebSocketLocalReturn {
    wsRef: React.RefObject<WebSocketClient | null>;
    requestBoardList: () => void;
    checkBridgeUpdateNow: () => void;
}

const DEFAULT_POLLING_INTERVAL = 5000;
// check_bridge_update_nowへの応答が来ないケース(未接続、旧ブリッジ、更新適用によるブリッジ
// プロセス再起動)を救済するためのタイムアウト。これが無いと呼び出し元の「確認中...」表示が
// 応答が来るまで永久に残ってしまう。
const BRIDGE_UPDATE_CHECK_TIMEOUT_MS = 10000;

export function useBoardWebSocketLocal(options: UseBoardWebSocketLocalOptions): UseBoardWebSocketLocalReturn {
    const {
        enabled,
        onLog,
        onBoardsDetected,
        onWebSocketConnected,
        onBridgeConnected,
        hasSelectedBoard,
        selectedBoard,
        onPortUpdate,
        pollingInterval = DEFAULT_POLLING_INTERVAL,
        onUploadEvent,
        getActiveRequestId,
        onOnlineIdsUpdated,
        onBridgeVersion,
        onLatestBridgeVersion,
        onBridgeUpdateCheckResult,
        onSerialMonitorStatus,
    } = options;

    const wsRef = useRef<WebSocketClient | null>(null);
    const previousBoardsRef = useRef<DetectedBoard[]>([]);
    const lastErrorMessageRef = useRef<string | null>(null);
    const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bridgeUpdateCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onLogRef = useRef(onLog);
    const onBoardsDetectedRef = useRef(onBoardsDetected);
    const onWebSocketConnectedRef = useRef(onWebSocketConnected);
    const onBridgeConnectedRef = useRef(onBridgeConnected);
    const onPortUpdateRef = useRef(onPortUpdate);
    const onUploadEventRef = useRef(onUploadEvent);
    const onBridgeVersionRef = useRef(onBridgeVersion);
    const onLatestBridgeVersionRef = useRef(onLatestBridgeVersion);
    const onBridgeUpdateCheckResultRef = useRef(onBridgeUpdateCheckResult);
    const onSerialMonitorStatusRef = useRef(onSerialMonitorStatus);
    const hasSelectedBoardRef = useRef(hasSelectedBoard);
    const selectedBoardRef = useRef<'orange' | 'blue'>(selectedBoard);
    const getActiveRequestIdRef = useRef(getActiveRequestId);

    useEffect(() => {
        onLogRef.current = onLog;
        onBoardsDetectedRef.current = onBoardsDetected;
        onWebSocketConnectedRef.current = onWebSocketConnected;
        onBridgeConnectedRef.current = onBridgeConnected;
        onPortUpdateRef.current = onPortUpdate;
        onUploadEventRef.current = onUploadEvent;
        onBridgeVersionRef.current = onBridgeVersion;
        onLatestBridgeVersionRef.current = onLatestBridgeVersion;
        onBridgeUpdateCheckResultRef.current = onBridgeUpdateCheckResult;
        onSerialMonitorStatusRef.current = onSerialMonitorStatus;
    }, [
        onLog,
        onBoardsDetected,
        onWebSocketConnected,
        onBridgeConnected,
        onPortUpdate,
        onUploadEvent,
        onOnlineIdsUpdated,
        onBridgeVersion,
        onLatestBridgeVersion,
        onBridgeUpdateCheckResult,
        onSerialMonitorStatus,
    ]);

    useEffect(() => {
        hasSelectedBoardRef.current = hasSelectedBoard;
        selectedBoardRef.current = selectedBoard;
    }, [hasSelectedBoard, selectedBoard]);

    useEffect(() => {
        getActiveRequestIdRef.current = getActiveRequestId;
    }, [getActiveRequestId]);

    const requestBoardList = useCallback(() => {
        if (wsRef.current?.isConnected) {
            wsRef.current.send({ type: 'request_board_list' });
        }
    }, []);

    const checkBridgeUpdateNow = useCallback(() => {
        if (bridgeUpdateCheckTimeoutRef.current) {
            clearTimeout(bridgeUpdateCheckTimeoutRef.current);
            bridgeUpdateCheckTimeoutRef.current = null;
        }
        if (!wsRef.current?.isConnected) {
            // 未接続時は応答が来ようがないため、即座に失敗として通知する。
            onBridgeUpdateCheckResultRef.current?.({
                update_available: false,
                message: 'スクーミーブリッジに接続できていません',
            });
            return;
        }
        wsRef.current.send({ type: 'check_bridge_update_now' });
        // 旧ブリッジ(未対応)・更新適用によるブリッジ再起動時は応答が来ないため、
        // タイムアウトで「確認中...」表示を解除する。
        bridgeUpdateCheckTimeoutRef.current = setTimeout(() => {
            bridgeUpdateCheckTimeoutRef.current = null;
            onBridgeUpdateCheckResultRef.current?.({
                update_available: false,
                message: '応答がありませんでした(未対応のブリッジか、更新適用のため再起動中の可能性があります)',
            });
        }, BRIDGE_UPDATE_CHECK_TIMEOUT_MS);
    }, []);

    const closeConnection = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    useEffect(() => {
        // 接続条件を満たしていない場合は切断
        if (!enabled) {
            closeConnection();
            return;
        }
        const ws = new WebSocketClient('/board', {
            preferLocalhost: true,
            onOpen: () => {
                if (disconnectTimerRef.current !== null) {
                    clearTimeout(disconnectTimerRef.current);
                    disconnectTimerRef.current = null;
                }
                onLogRef.current(`🔅ブリッジサーバに接続しました。`);
                lastErrorMessageRef.current = null;
                onWebSocketConnectedRef.current(true);
                ws.send({ type: 'request_board_list' });
                ws.send({ type: 'request_bridge_version' });
                ws.send({ type: 'request_latest_bridge_version' });
            },

            onMessage: (data: any) => {
                try {
                    if (data?.type === 'boards_detected') {
                        const boards: DetectedBoard[] = data.boards || [];

                        const hasChanged =
                            boards.length !== previousBoardsRef.current.length ||
                            JSON.stringify(boards) !== JSON.stringify(previousBoardsRef.current);

                        if (hasChanged) {
                            // Bluetoothはペアリング済みなら実機の電源が入っていなくてもOS上に
                            // 仮想COMポートとして残り続けるため、USB(実際に挿さっている)とは
                            // 分けて表示しないと「何も挿していないのに検出された」ように誤解されうる。
                            const usbCount = boards.filter((b) => !b.is_bluetooth).length;
                            const bluetoothCount = boards.length - usbCount;
                            const parts = [
                                usbCount > 0 ? `USB: ${usbCount}台` : null,
                                bluetoothCount > 0 ? `Bluetoothペアリング済み: ${bluetoothCount}台` : null,
                            ].filter(Boolean);
                            onLogRef.current(
                                parts.length > 0
                                    ? `スクーミーボードが検出されました。(${parts.join('、')})`
                                    : 'スクーミーボードが検出されなくなりました。',
                            );
                            previousBoardsRef.current = boards;
                            onBoardsDetectedRef.current(boards);
                        }
                        onBridgeConnectedRef.current(true);
                        if (hasSelectedBoardRef.current) {
                            // ペアリング済みのBluetooth仮想COMポートはUSBを抜いた後もOS上に
                            // 残り続けるため、ここでUSBのポートと誤ってマッチしないよう除外する。
                            const matched = boards.find((b) => !b.is_bluetooth && b.type === selectedBoardRef.current);
                            onPortUpdateRef.current(matched?.port ?? null);
                        }
                        return;
                    }

                    if (data?.type === 'upload_progress') {
                        const local = typeof data.progress === 'number' ? data.progress : Number(data.progress ?? 0);
                        const phase: 'sending' | 'writing' = local <= 60 ? 'sending' : 'writing';

                        onUploadEventRef.current({
                            source: 'localhost',
                            kind: 'progress',
                            progress: local,
                            phase,
                            message: typeof data.message === 'string' ? data.message : undefined,
                        });
                        return;
                    }

                    if (data?.type === 'upload_result') {
                        const success = !!data.success;

                        if (success) {
                            onLogRef.current('🎉スクーミーボードに書き込みが完了しました！');
                        } else {
                            const errMsg = (data.message as string) || '書き込みに失敗しました。再度お試しください。';
                            onLogRef.current(`☔${errMsg}`);
                        }

                        onUploadEventRef.current({
                            source: 'localhost',
                            kind: 'result',
                            success,
                            message: success ? '書き込み完了！' : undefined,
                            errorMessage: success ? undefined : (data.message as string) || 'エラー',
                        });
                        return;
                    }

                    if (data?.type === 'pong') {
                        onLogRef.current(`🏓Pong: ${data.message}`);
                        return;
                    }

                    if (data?.type === 'bridge_version' && typeof data.version === 'string') {
                        onBridgeVersionRef.current?.(data.version);
                        return;
                    }

                    if (data?.type === 'latest_bridge_version') {
                        onLatestBridgeVersionRef.current?.(
                            typeof data.version === 'string' ? data.version : null,
                        );
                        return;
                    }

                    if (data?.type === 'bridge_update_check_result') {
                        if (bridgeUpdateCheckTimeoutRef.current) {
                            clearTimeout(bridgeUpdateCheckTimeoutRef.current);
                            bridgeUpdateCheckTimeoutRef.current = null;
                        }
                        onBridgeUpdateCheckResultRef.current?.({
                            update_available: !!data.update_available,
                            latest_version: typeof data.latest_version === 'string' ? data.latest_version : undefined,
                            message: typeof data.message === 'string' ? data.message : '',
                        });
                        return;
                    }

                    if (
                        data?.type === 'serial_monitor_status' &&
                        typeof data.port === 'string' &&
                        typeof data.status === 'string'
                    ) {
                        onSerialMonitorStatusRef.current?.(data.port, data.status);
                        return;
                    }

                    if (data?.type === 'error') {
                        const errorMsg = `☔${data.message}`;
                        if (lastErrorMessageRef.current !== errorMsg) {
                            onLogRef.current(errorMsg);
                            lastErrorMessageRef.current = errorMsg;
                        }
                        return;
                    }
                } catch (err) {
                    onLogRef.current('☔WS onMessage crashed (see console)');
                }
            },

            onError: () => {
                const errorMsg = '☔ブリッジサーバに接続できません。';
                if (lastErrorMessageRef.current !== errorMsg) {
                    onLogRef.current(errorMsg);
                    lastErrorMessageRef.current = errorMsg;
                }
                onWebSocketConnectedRef.current(false);
                onBridgeConnectedRef.current(false);
                onBoardsDetectedRef.current([]);
                onPortUpdateRef.current(null);
                previousBoardsRef.current = [];
            },

            onClose: () => {
                // 再接続の瞬断でモニターが切断/再接続を繰り返さないよう遅延クリア。
                // onCloseが3秒以内に複数回発生すると古いtimerが残ったまま上書きされ、再接続後に
                // 古いtimerが発火して誤って切断状態に戻す不具合があったため、張り直す前に必ず破棄する。
                if (disconnectTimerRef.current !== null) {
                    clearTimeout(disconnectTimerRef.current);
                }
                disconnectTimerRef.current = setTimeout(() => {
                    disconnectTimerRef.current = null;
                    onWebSocketConnectedRef.current(false);
                    onBridgeConnectedRef.current(false);
                    onBoardsDetectedRef.current([]);
                    onPortUpdateRef.current(null);
                    previousBoardsRef.current = [];
                }, 3000);
            },

            autoReconnect: true,
            silentReconnect: true,
        });

        ws.connect();
        wsRef.current = ws;

        const pollingIntervalId = setInterval(() => {
            if (ws.isConnected) {
                ws.send({ type: 'request_board_list' });
            }
        }, pollingInterval);

        return () => {
            clearInterval(pollingIntervalId);
            if (disconnectTimerRef.current !== null) {
                clearTimeout(disconnectTimerRef.current);
                disconnectTimerRef.current = null;
            }
            if (bridgeUpdateCheckTimeoutRef.current !== null) {
                clearTimeout(bridgeUpdateCheckTimeoutRef.current);
                bridgeUpdateCheckTimeoutRef.current = null;
            }
            ws.close();
        };
    }, [pollingInterval]);

    return { wsRef, requestBoardList, checkBridgeUpdateNow };
}
